import {toCentsFromMoney} from "../../../../../../../../lib/Money.ts";
import type {BackendAPITypes} from "../../../../../../../../generated/blueprint.ts";
import type {CustomerBankAccount} from "../../../../../../../../api/customerBankAccounts/CustomerBankAccount.ts";
import type {
    CreditCardStatementDocumentOption,
    CreditCardStatementLine,
    MissingDocumentRequest,
} from "../../../types.ts";

export const CREDIT_CARD_SETTLEMENT_CURRENCY = "EUR";

export function newCreditCardStatementLine(key: number): CreditCardStatementLine {
    return {key, relationName: "", description: "", amount: undefined, bookingDate: ""};
}

export function lineCents(line: CreditCardStatementLine): number {
    return line.amount ? toCentsFromMoney(line.amount) : 0;
}

export function differenceCents(
    settlementSignedCents: number,
    statementLines: CreditCardStatementLine[],
): number {
    const filled = statementLines.reduce((total, line) => total + lineCents(line), 0);
    return settlementSignedCents - filled;
}

export function canSubmitSettlement(params: {
    selectedDocumentId: string;
    selectedAccount: CustomerBankAccount | null;
    statementLines: CreditCardStatementLine[];
    settlementSignedCents: number;
    isSubmitting: boolean;
    isLoading: boolean;
}): boolean {
    const statementLinesValid = params.statementLines.length > 0 && params.statementLines.every(
        (line) =>
            line.relationName.trim() !== ""
            && line.description.trim() !== ""
            && lineCents(line) !== 0
            && line.bookingDate !== "",
    );

    return Boolean(
        params.selectedDocumentId
        && params.selectedAccount
        && statementLinesValid
        && differenceCents(params.settlementSignedCents, params.statementLines) === 0
        && !params.isSubmitting
        && !params.isLoading,
    );
}

export function mapCreditCardStatements(
    documents: {id: string; file_name: string; created_at: string}[],
): CreditCardStatementDocumentOption[] {
    return documents.map((document) => ({
        id: document.id,
        fileName: document.file_name,
        createdAt: document.created_at,
    }));
}

export interface CreditCardSettlementFormPayload {
    documentId: string;
    creditCardAccountId: string;
    statementLines: CreditCardStatementLine[];
}

export function buildSettlementSubmitPayload(
    businessId: string,
    settlementTransaction: MissingDocumentRequest,
    payload: CreditCardSettlementFormPayload,
): BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerProcessCreditCardSettlementPostRequestBody {
    return {
        client_business_id: businessId,
        document_id: payload.documentId,
        credit_card_account_id: payload.creditCardAccountId,
        settlement_transaction_id: settlementTransaction.transaction_id,
        statement_lines: payload.statementLines.map((line) => ({
            relation_name: line.relationName.trim(),
            description: line.description.trim(),
            amount_cents: lineCents(line),
            booking_date: line.bookingDate,
        })),
    };
}
